import { memo } from 'react';
import { Badge, Table } from 'react-bootstrap';

import type { Facility, SortableField, SortConfig } from '@/types';

import styles from './FacilitiesTable.module.scss';

interface FacilitiesTableProps {
  readonly facilities: readonly Facility[];
  readonly sortConfig?: SortConfig;
  readonly onSort?: (field: SortableField) => void;
  readonly onFacilityClick?: (facility: Facility) => void;
}

interface ColumnDef {
  readonly field: SortableField;
  readonly label: string;
  readonly className?: string;
  readonly hideOnMobile?: boolean;
  readonly hideOnTablet?: boolean;
}

const COLUMNS: readonly ColumnDef[] = [
  { field: 'id', label: 'ID' },
  { field: 'name', label: 'Name' },
  { field: 'facility_type', label: 'Type' },
  { field: 'status', label: 'Status' },
  { field: 'city', label: 'Address', hideOnTablet: true },
  { field: 'city', label: 'City', hideOnMobile: true },
  { field: 'province', label: 'Prov' },
  { field: 'phone', label: 'Phone', hideOnTablet: true },
  { field: 'email', label: 'Email', hideOnTablet: true },
];

function getStatusVariant(
  status: string | null
): 'success' | 'warning' | 'danger' | 'secondary' {
  if (!status) return 'secondary';
  const normalized = status.toLowerCase();
  if (
    normalized === 'licensed' ||
    normalized === 'active' ||
    normalized === 'open'
  ) {
    return 'success';
  }
  if (normalized === 'pending' || normalized === 'inactive') {
    return 'warning';
  }
  if (normalized === 'closed' || normalized === 'revoked') {
    return 'danger';
  }
  return 'secondary';
}

function formatStatus(status: string | null): string {
  if (!status) return '-';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

function getProvinceVariant(province: string): 'primary' | 'success' | 'warning' | 'info' | 'secondary' {
  switch (province.toUpperCase()) {
    case 'BC':
      return 'primary';
    case 'ON':
      return 'success';
    case 'AB':
      return 'warning';
    default:
      return 'secondary';
  }
}

function formatFacilityType(type: string | null): string {
  if (!type) return '-';
  return type
    .replace(/^Licensed\s+/i, '')
    .replace(/\s+Centre$/i, ' Centre')
    .replace(/\s+Center$/i, ' Center');
}

function formatFacilityTypes(facility: Facility): {
  display: string;
  tooltip: string;
  hasMultiple: boolean;
} {
  const types = facility.facility_types;
  if (!types || types.length === 0) {
    const formatted = formatFacilityType(facility.facility_type);
    return { display: formatted, tooltip: formatted, hasMultiple: false };
  }

  const formattedTypes = types.map(formatFacilityType);
  const display = formattedTypes[0];
  const tooltip = formattedTypes.join(', ');
  return { display, tooltip, hasMultiple: types.length > 1 };
}

function buildGoogleSearchUrl(facility: Facility): string {
  const parts = [
    facility.name,
    facility.address.street_1,
    facility.address.city,
    facility.address.province,
  ];
  const query = parts.filter(Boolean).join(' ');
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

const SortHeader = memo<{
  column: ColumnDef;
  sortConfig?: SortConfig;
  onSort?: (field: SortableField) => void;
}>(function SortHeader({ column, sortConfig, onSort }) {
  const isActive = sortConfig?.field === column.field;
  const isAsc = isActive && sortConfig?.order === 'asc';
  const isDesc = isActive && sortConfig?.order === 'desc';

  const handleClick = (): void => {
    onSort?.(column.field);
  };

  let responsiveClass = '';
  if (column.hideOnMobile) {
    responsiveClass = 'd-none d-md-table-cell';
  } else if (column.hideOnTablet) {
    responsiveClass = 'd-none d-lg-table-cell';
  }

  const classNames = [
    styles.sortableHeader,
    column.className,
    responsiveClass,
    isActive ? styles.activeSort : '',
  ].filter(Boolean).join(' ');

  return (
    <th className={classNames} onClick={handleClick}>
      <div className={styles.headerContent}>
        <span>{column.label}</span>
        <span className={styles.sortIcon}>
          {isAsc && <i className="bi bi-sort-up" />}
          {isDesc && <i className="bi bi-sort-down" />}
          {!isActive && <i className="bi bi-chevron-expand" />}
        </span>
      </div>
    </th>
  );
});

const TableRow = memo<{
  facility: Facility;
  onFacilityClick?: (facility: Facility) => void;
}>(function TableRow({ facility, onFacilityClick }) {
  const handleClick = (): void => {
    onFacilityClick?.(facility);
  };

  const { display: typeDisplay, hasMultiple } = formatFacilityTypes(facility);
  const extraCount = (facility.facility_types?.length ?? 1) - 1;

  return (
    <tr
      className={onFacilityClick ? styles.clickableRow : undefined}
      onClick={handleClick}
    >
      <td>
        <span className={styles.idText}>{facility.id}</span>
      </td>
      <td>
        <span className={styles.facilityName}>{facility.name}</span>
      </td>
      <td>
        <span className={styles.typeText}>{typeDisplay}</span>
        {hasMultiple && (
          <Badge bg="secondary" className={styles.multiTypeBadge}>
            +{extraCount}
          </Badge>
        )}
      </td>
      <td>
        <Badge bg={getStatusVariant(facility.status)}>
          {formatStatus(facility.status)}
        </Badge>
      </td>
      <td className="d-none d-lg-table-cell">
        {facility.address.street_1 || '-'}
      </td>
      <td className="d-none d-md-table-cell">
        {facility.address.city || '-'}
      </td>
      <td>
        <Badge bg={getProvinceVariant(facility.province)} className={styles.provinceBadge}>
          {facility.province}
        </Badge>
      </td>
      <td className="d-none d-lg-table-cell">
        {facility.phone || '-'}
      </td>
      <td className="d-none d-lg-table-cell">
        {facility.email ? (
          <a
            href={`mailto:${facility.email}`}
            onClick={(e) => e.stopPropagation()}
            className={styles.emailLink}
          >
            {facility.email}
          </a>
        ) : (
          '-'
        )}
      </td>
      <td className={styles.searchCell}>
        <a
          href={buildGoogleSearchUrl(facility)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.searchLink}
          onClick={(e) => e.stopPropagation()}
          title="Search on Google"
        >
          <i className="bi bi-search" />
        </a>
      </td>
    </tr>
  );
});

export const FacilitiesTable = memo<FacilitiesTableProps>(
  function FacilitiesTable({ facilities, sortConfig, onSort, onFacilityClick }) {
    if (facilities.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <i className="bi bi-building-slash" />
          </div>
          <p className={styles.emptyText}>
            No facilities found matching your criteria.
          </p>
        </div>
      );
    }

    return (
      <div className={styles.tableContainer}>
        <Table hover className={styles.facilitiesTable}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <SortHeader
                  key={column.field}
                  column={column}
                  sortConfig={sortConfig}
                  onSort={onSort}
                />
              ))}
              <th></th>
            </tr>
          </thead>
            <tbody>
              {facilities.map((facility) => (
                <TableRow
                  key={facility.id}
                  facility={facility}
                  onFacilityClick={onFacilityClick}
                />
              ))}
            </tbody>
          </Table>
      </div>
    );
  }
);
